import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import DeviceHubRoundedIcon from '@mui/icons-material/DeviceHubRounded';
import GroupRoundedIcon from '@mui/icons-material/GroupRounded';
import InsightsRoundedIcon from '@mui/icons-material/InsightsRounded';
import ScienceRoundedIcon from '@mui/icons-material/ScienceRounded';
import SmartToyRoundedIcon from '@mui/icons-material/SmartToyRounded';
import StorageRoundedIcon from '@mui/icons-material/StorageRounded';
import TimelineRoundedIcon from '@mui/icons-material/TimelineRounded';
import {
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { SessionTokenRow, StudyTokenRow, TokenUsageOverview, UserTokenRow } from 'api/adminCopilot';
import AdminBarChart from 'components/atoms/AdminAnalyticsBarChart';
import { ADMIN_TABLE_DEFAULT_PAGE_SIZE, ADMIN_TABLE_PAGE_SIZE_OPTIONS } from 'constants/adminTables';
import { filter, join, map, sumBy } from 'es-toolkit/compat';
import React, { useState } from 'react';
import { useNavigateBackFromAdmin } from 'utils/adminNavigation';
import useUsersById from 'utils/queryHooks/useUsersById';
import { wrapAdminPage } from '../AdminPageLayout';

import DailyTrendChart from './DailyTrendChart';
import { useTokenUsageOverview } from './useTokenUsageOverview';

type TabValue = 'overview' | 'users' | 'tools' | 'agents' | 'studies' | 'models' | 'sessions';
type TokenGroupingValue = 'user' | 'session' | 'study' | 'tool' | 'agent' | 'model';
type SessionGroupingValue = 'user' | 'study' | 'model' | 'tool' | 'agent';

const getUserDisplayName = (user: UserTokenRow, getUserNameById: (userId?: string) => string | undefined): string => {
  const userNameFromDirectory = getUserNameById(user.userId);

  return userNameFromDirectory && userNameFromDirectory !== user.userId
    ? userNameFromDirectory
    : user.userName || user.userEmail || userNameFromDirectory || user.userId;
};

const getStudyDisplayName = (study: StudyTokenRow): string => study.studyName || study.studyId;

const getSessionDisplayName = (session: SessionTokenRow): string => session.title || session.sessionId.slice(0, 8);

const getSessionTraceTokenTotal = (session: SessionTokenRow): number =>
  session.totalCacheReadTokens + session.totalCacheWriteTokens;

const formatJoinedValues = (values: string[]): string => (values.length > 0 ? join(values, ', ') : '-');

const TOKEN_GROUP_OPTIONS: { value: TokenGroupingValue; label: string }[] = [
  { value: 'user', label: 'User' },
  { value: 'session', label: 'Session' },
  { value: 'study', label: 'Study' },
  { value: 'tool', label: 'Tool' },
  { value: 'agent', label: 'Agent' },
  { value: 'model', label: 'Model' },
];

const SESSION_GROUP_OPTIONS: { value: SessionGroupingValue; label: string }[] = [
  { value: 'user', label: 'User' },
  { value: 'study', label: 'Study' },
  { value: 'model', label: 'Model' },
  { value: 'tool', label: 'Tool' },
  { value: 'agent', label: 'Agent' },
];

interface StatCardProps {
  label: string;
  value: string | number;
}

const StatCard: React.FC<StatCardProps> = ({ label, value }) => (
  <Card variant="outlined" sx={{ minWidth: 140, flex: 1 }}>
    <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
      <Typography variant="caption" color="text.secondary" noWrap>
        {label}
      </Typography>
      <Typography variant="h5" fontWeight={600}>
        {typeof value === 'number' ? value.toLocaleString() : value}
      </Typography>
    </CardContent>
  </Card>
);

const MetricCardRow: React.FC<{ metrics: StatCardProps[] }> = ({ metrics }) => (
  <Grid container spacing={2} sx={{ mb: 3 }}>
    {map(metrics, (metric) => (
      <Grid item xs={6} sm={4} md={3} key={metric.label}>
        <StatCard label={metric.label} value={metric.value} />
      </Grid>
    ))}
  </Grid>
);

const SummaryCards: React.FC<{ data: TokenUsageOverview }> = ({ data }) => {
  const { summary } = data;
  return (
    <Grid container spacing={2} sx={{ mb: 3 }}>
      <Grid item xs={6} sm={4} md={2}>
        <StatCard label="Total Sessions" value={summary.totalSessions} />
      </Grid>
      <Grid item xs={6} sm={4} md={2}>
        <StatCard label="Unique Users" value={summary.uniqueUsers} />
      </Grid>
      <Grid item xs={6} sm={4} md={2}>
        <StatCard label="Total Tokens" value={summary.totalTokens} />
      </Grid>
      <Grid item xs={6} sm={4} md={2}>
        <StatCard label="Total Turns" value={summary.totalTurns} />
      </Grid>
      <Grid item xs={6} sm={4} md={2}>
        <StatCard label="Avg / Session" value={Math.round(summary.avgTokensPerSession).toLocaleString()} />
      </Grid>
      <Grid item xs={6} sm={4} md={2}>
        <StatCard label="Max in Session" value={summary.maxTokensInSession} />
      </Grid>
    </Grid>
  );
};

const OverviewTab: React.FC<{
  data: TokenUsageOverview;
  getUserLabel: (user: UserTokenRow) => string;
}> = ({ data, getUserLabel }) => {
  const theme = useTheme();
  const [tokenGrouping, setTokenGrouping] = useState<TokenGroupingValue>('user');
  const [sessionGrouping, setSessionGrouping] = useState<SessionGroupingValue>('user');
  const totalCacheReadTokens = sumBy(data.byAgent, 'totalCacheReadTokens');
  const totalCacheWriteTokens = sumBy(data.byAgent, 'totalCacheWriteTokens');

  const tokenChart = (() => {
    switch (tokenGrouping) {
      case 'user':
        return {
          labels: map(data.byUser, getUserLabel),
          values: map(data.byUser, 'totalTokens'),
          title: 'Token Usage by User',
          color: theme.palette.primary.main,
          horizontal: true,
        };
      case 'session':
        return {
          labels: map(data.topSessions, (sessionRow) => sessionRow.title || sessionRow.sessionId.slice(0, 8)),
          values: map(data.topSessions, 'totalTokens'),
          title: 'Token Usage by Session',
          color: theme.palette.secondary.main,
          horizontal: false,
        };
      case 'study':
        return {
          labels: map(data.byStudy, getStudyDisplayName),
          values: map(data.byStudy, 'totalTokens'),
          title: 'Token Usage by Study',
          color: theme.palette.info.main,
          horizontal: true,
        };
      case 'tool':
        return {
          labels: map(data.byTool, 'toolName'),
          values: map(data.byTool, 'totalTokens'),
          title: 'Token Usage by Tool',
          color: theme.palette.success.main,
          horizontal: true,
        };
      case 'agent':
        return {
          labels: map(data.byAgent, 'agentName'),
          values: map(data.byAgent, 'totalTokens'),
          title: 'Token Usage by Agent',
          color: theme.palette.warning.main,
          horizontal: true,
        };
      case 'model':
        return {
          labels: map(data.byModel, (modelRow) => `${modelRow.model} (${modelRow.provider})`),
          values: map(data.byModel, 'totalTokens'),
          title: 'Token Usage by Model',
          color: theme.palette.error.main,
          horizontal: true,
        };
      default:
        return {
          labels: [],
          values: [],
          title: 'Token Usage',
          color: theme.palette.primary.main,
          horizontal: true,
        };
    }
  })();

  const sessionChart = (() => {
    switch (sessionGrouping) {
      case 'user':
        return {
          labels: map(data.byUser, getUserLabel),
          values: map(data.byUser, 'sessionCount'),
          title: 'Sessions by User',
          color: theme.palette.secondary.main,
        };
      case 'study':
        return {
          labels: map(data.byStudy, getStudyDisplayName),
          values: map(data.byStudy, 'sessionCount'),
          title: 'Sessions by Study',
          color: theme.palette.info.main,
        };
      case 'model':
        return {
          labels: map(data.byModel, (modelRow) => `${modelRow.model} (${modelRow.provider})`),
          values: map(data.byModel, 'sessionCount'),
          title: 'Sessions by Model',
          color: theme.palette.warning.main,
        };
      case 'tool':
        return {
          labels: map(data.byTool, 'toolName'),
          values: map(data.byTool, 'sessionCount'),
          title: 'Sessions by Tool',
          color: theme.palette.success.main,
        };
      case 'agent':
        return {
          labels: map(data.byAgent, 'agentName'),
          values: map(data.byAgent, 'sessionCount'),
          title: 'Sessions by Agent',
          color: theme.palette.error.main,
        };
      default:
        return {
          labels: [],
          values: [],
          title: 'Sessions',
          color: theme.palette.secondary.main,
        };
    }
  })();

  return (
    <>
      <MetricCardRow
        metrics={[
          { label: 'Input Tokens', value: data.summary.totalInputTokens },
          { label: 'Output Tokens', value: data.summary.totalOutputTokens },
          { label: 'Cache Read Tokens', value: totalCacheReadTokens },
          { label: 'Cache Write Tokens', value: totalCacheWriteTokens },
        ]}
      />
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <DailyTrendChart data={data.dailyTrend} />
        </Grid>
        <Grid item xs={12} md={6}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  gap: 2,
                  mb: 2,
                  flexWrap: 'wrap',
                }}
              >
                <Box>
                  <Typography variant="subtitle1" fontWeight={600}>
                    Token Usage Breakdown
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Input, output, and aggregate totals stay in the same payload while you regroup the chart.
                  </Typography>
                </Box>
                <FormControl size="small" sx={{ minWidth: 160 }}>
                  <InputLabel>Group By</InputLabel>
                  <Select
                    value={tokenGrouping}
                    label="Group By"
                    onChange={(event) => setTokenGrouping(event.target.value as TokenGroupingValue)}
                  >
                    {map(TOKEN_GROUP_OPTIONS, (option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              <AdminBarChart
                labels={tokenChart.labels}
                values={tokenChart.values}
                title={tokenChart.title}
                color={tokenChart.color}
                horizontal={tokenChart.horizontal}
                height={360}
              />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  gap: 2,
                  mb: 2,
                  flexWrap: 'wrap',
                }}
              >
                <Box>
                  <Typography variant="subtitle1" fontWeight={600}>
                    Session Breakdown
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Group the same overview by user, study, model, tool, or agent without a refetch.
                  </Typography>
                </Box>
                <FormControl size="small" sx={{ minWidth: 160 }}>
                  <InputLabel>Group By</InputLabel>
                  <Select
                    value={sessionGrouping}
                    label="Group By"
                    onChange={(event) => setSessionGrouping(event.target.value as SessionGroupingValue)}
                  >
                    {map(SESSION_GROUP_OPTIONS, (option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              <AdminBarChart
                labels={sessionChart.labels}
                values={sessionChart.values}
                title={sessionChart.title}
                color={sessionChart.color}
                horizontal
                height={360}
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </>
  );
};

const UsersTab: React.FC<{
  data: TokenUsageOverview;
  getUserLabel: (user: UserTokenRow) => string;
}> = ({ data, getUserLabel }) => {
  const theme = useTheme();
  const internal = filter(data.byUser, 'isInternal');
  const external = filter(data.byUser, (u) => !u.isInternal);

  return (
    <>
      <MetricCardRow
        metrics={[
          { label: 'Input Tokens', value: sumBy(data.byUser, 'totalInputTokens') },
          { label: 'Output Tokens', value: sumBy(data.byUser, 'totalOutputTokens') },
          { label: 'Total Tokens', value: sumBy(data.byUser, 'totalTokens') },
          { label: 'Avg / Session', value: Math.round(data.summary.avgTokensPerSession).toLocaleString() },
        ]}
      />
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <AdminBarChart
            labels={map(internal, getUserLabel)}
            values={map(internal, 'totalTokens')}
            title="Internal Users — Total Tokens"
            color={theme.palette.info.main}
            horizontal
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <AdminBarChart
            labels={map(external, getUserLabel)}
            values={map(external, 'totalTokens')}
            title="External Users — Total Tokens"
            color={theme.palette.warning.main}
            horizontal
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <AdminBarChart
            labels={map(data.byUser, getUserLabel)}
            values={map(data.byUser, 'totalInputTokens')}
            title="Input Tokens by User"
            color={theme.palette.primary.main}
            horizontal
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <AdminBarChart
            labels={map(data.byUser, getUserLabel)}
            values={map(data.byUser, 'totalOutputTokens')}
            title="Output Tokens by User"
            color={theme.palette.success.main}
            horizontal
          />
        </Grid>
      </Grid>
    </>
  );
};

const ToolsTab: React.FC<{ data: TokenUsageOverview }> = ({ data }) => {
  const theme = useTheme();
  const totalToolCalls = sumBy(data.byTool, 'callCount');
  const weightedAverageDuration =
    totalToolCalls > 0
      ? Math.round(sumBy(data.byTool, (toolRow) => toolRow.avgDurationMs * toolRow.callCount) / totalToolCalls)
      : 0;

  return (
    <>
      <MetricCardRow
        metrics={[
          { label: 'Tools Used', value: data.byTool.length },
          { label: 'Tool Calls', value: totalToolCalls },
          { label: 'Input Tokens', value: sumBy(data.byTool, 'totalInputTokens') },
          { label: 'Output Tokens', value: sumBy(data.byTool, 'totalOutputTokens') },
          { label: 'Total Tokens', value: sumBy(data.byTool, 'totalTokens') },
          { label: 'Avg Duration', value: `${weightedAverageDuration}ms` },
        ]}
      />
      <Alert severity="info" sx={{ mb: 3 }}>
        Tool traces always emit call counts and durations. Input and output token totals only appear when the tool trace
        entry includes them.
      </Alert>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <AdminBarChart
            labels={map(data.byTool, 'toolName')}
            values={map(data.byTool, 'callCount')}
            title="Call Count by Tool"
            color={theme.palette.secondary.main}
            horizontal
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <AdminBarChart
            labels={map(data.byTool, 'toolName')}
            values={map(data.byTool, 'avgDurationMs')}
            title="Avg Duration (ms) by Tool"
            color={theme.palette.warning.main}
            horizontal
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <AdminBarChart
            labels={map(data.byTool, 'toolName')}
            values={map(data.byTool, 'totalInputTokens')}
            title="Input Tokens by Tool"
            color={theme.palette.primary.main}
            horizontal
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <AdminBarChart
            labels={map(data.byTool, 'toolName')}
            values={map(data.byTool, 'totalOutputTokens')}
            title="Output Tokens by Tool"
            color={theme.palette.success.main}
            horizontal
          />
        </Grid>
      </Grid>
    </>
  );
};

const AgentsTab: React.FC<{ data: TokenUsageOverview }> = ({ data }) => {
  const theme = useTheme();
  return (
    <>
      <MetricCardRow
        metrics={[
          { label: 'Input Tokens', value: sumBy(data.byAgent, 'totalInputTokens') },
          { label: 'Output Tokens', value: sumBy(data.byAgent, 'totalOutputTokens') },
          { label: 'Cache Read Tokens', value: sumBy(data.byAgent, 'totalCacheReadTokens') },
          { label: 'Cache Write Tokens', value: sumBy(data.byAgent, 'totalCacheWriteTokens') },
        ]}
      />
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <AdminBarChart
            labels={map(data.byAgent, 'agentName')}
            values={map(data.byAgent, 'totalTokens')}
            title="Total Tokens by Agent"
            horizontal
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <AdminBarChart
            labels={map(data.byAgent, 'agentName')}
            values={map(data.byAgent, 'callCount')}
            title="Model Calls by Agent"
            color={theme.palette.secondary.main}
            horizontal
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <AdminBarChart
            labels={map(data.byAgent, 'agentName')}
            values={map(data.byAgent, 'totalInputTokens')}
            title="Input Tokens by Agent"
            color={theme.palette.primary.main}
            horizontal
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <AdminBarChart
            labels={map(data.byAgent, 'agentName')}
            values={map(data.byAgent, 'totalOutputTokens')}
            title="Output Tokens by Agent"
            color={theme.palette.success.main}
            horizontal
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <AdminBarChart
            labels={map(data.byAgent, 'agentName')}
            values={map(data.byAgent, 'totalCacheReadTokens')}
            title="Cache Read Tokens by Agent"
            color={theme.palette.info.main}
            horizontal
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <AdminBarChart
            labels={map(data.byAgent, 'agentName')}
            values={map(data.byAgent, 'totalCacheWriteTokens')}
            title="Cache Write Tokens by Agent"
            color={theme.palette.warning.main}
            horizontal
          />
        </Grid>
      </Grid>
    </>
  );
};

const StudiesTab: React.FC<{ data: TokenUsageOverview }> = ({ data }) => {
  const theme = useTheme();
  return (
    <>
      <MetricCardRow
        metrics={[
          { label: 'Total Tokens', value: sumBy(data.byStudy, 'totalTokens') },
          { label: 'Sessions', value: sumBy(data.byStudy, 'sessionCount') },
          { label: 'Unique Users', value: sumBy(data.byStudy, 'uniqueUsers') },
          { label: 'Turns', value: sumBy(data.byStudy, 'totalTurns') },
        ]}
      />
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <AdminBarChart
            labels={map(data.byStudy, getStudyDisplayName)}
            values={map(data.byStudy, 'totalTokens')}
            title="Total Tokens by Study"
            horizontal
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <AdminBarChart
            labels={map(data.byStudy, getStudyDisplayName)}
            values={map(data.byStudy, 'sessionCount')}
            title="Sessions by Study"
            color={theme.palette.secondary.main}
            horizontal
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <AdminBarChart
            labels={map(data.byStudy, getStudyDisplayName)}
            values={map(data.byStudy, 'uniqueUsers')}
            title="Unique Users by Study"
            color={theme.palette.info.main}
            horizontal
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <AdminBarChart
            labels={map(data.byStudy, getStudyDisplayName)}
            values={map(data.byStudy, 'totalTurns')}
            title="Total Turns by Study"
            color={theme.palette.warning.main}
            horizontal
          />
        </Grid>
      </Grid>
    </>
  );
};

const SessionsTab: React.FC<{ data: TokenUsageOverview }> = ({ data }) => {
  const theme = useTheme();
  const sessionLabels = map(data.topSessions, getSessionDisplayName);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(ADMIN_TABLE_DEFAULT_PAGE_SIZE);
  const paginatedSessions = data.topSessions.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <>
      <MetricCardRow
        metrics={[
          { label: 'Input Tokens', value: sumBy(data.topSessions, 'inputTokens') },
          { label: 'Output Tokens', value: sumBy(data.topSessions, 'outputTokens') },
          { label: 'Cache Read Tokens', value: sumBy(data.topSessions, 'totalCacheReadTokens') },
          { label: 'Cache Write Tokens', value: sumBy(data.topSessions, 'totalCacheWriteTokens') },
          { label: 'Model Calls', value: sumBy(data.topSessions, 'modelCallCount') },
          { label: 'Tool Calls', value: sumBy(data.topSessions, 'toolCallCount') },
        ]}
      />
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <AdminBarChart
            labels={sessionLabels}
            values={map(data.topSessions, 'totalTokens')}
            title="Total Tokens by Session"
            color={theme.palette.primary.main}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <AdminBarChart
            labels={sessionLabels}
            values={map(data.topSessions, 'inputTokens')}
            title="Input Tokens by Session"
            color={theme.palette.info.main}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <AdminBarChart
            labels={sessionLabels}
            values={map(data.topSessions, 'outputTokens')}
            title="Output Tokens by Session"
            color={theme.palette.success.main}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <AdminBarChart
            labels={sessionLabels}
            values={map(data.topSessions, getSessionTraceTokenTotal)}
            title="Cache Tokens by Session Trace"
            color={theme.palette.warning.main}
          />
        </Grid>
        <Grid item xs={12}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" fontWeight={600}>
                Session Trace Details
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Top sessions now include trace-derived cache totals, model and tool call counts, tool execution time,
                and the agents and tools that participated in each turn set.
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Session</TableCell>
                      <TableCell align="right">Turns</TableCell>
                      <TableCell align="right">In</TableCell>
                      <TableCell align="right">Out</TableCell>
                      <TableCell align="right">Cache R</TableCell>
                      <TableCell align="right">Cache W</TableCell>
                      <TableCell align="right">Model Calls</TableCell>
                      <TableCell align="right">Tool Calls</TableCell>
                      <TableCell align="right">Tool Time</TableCell>
                      <TableCell>Agents</TableCell>
                      <TableCell>Tools</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {map(paginatedSessions, (sessionRow) => (
                      <TableRow key={sessionRow.sessionId}>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" fontWeight={600}>
                              {getSessionDisplayName(sessionRow)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {sessionRow.sessionId}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="right">{sessionRow.turnCount.toLocaleString()}</TableCell>
                        <TableCell align="right">{sessionRow.inputTokens.toLocaleString()}</TableCell>
                        <TableCell align="right">{sessionRow.outputTokens.toLocaleString()}</TableCell>
                        <TableCell align="right">{sessionRow.totalCacheReadTokens.toLocaleString()}</TableCell>
                        <TableCell align="right">{sessionRow.totalCacheWriteTokens.toLocaleString()}</TableCell>
                        <TableCell align="right">{sessionRow.modelCallCount.toLocaleString()}</TableCell>
                        <TableCell align="right">{sessionRow.toolCallCount.toLocaleString()}</TableCell>
                        <TableCell align="right">{sessionRow.totalToolDurationMs.toLocaleString()}ms</TableCell>
                        <TableCell>{formatJoinedValues(sessionRow.agents)}</TableCell>
                        <TableCell>{formatJoinedValues(sessionRow.tools)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={data.topSessions.length}
                page={page}
                rowsPerPage={rowsPerPage}
                rowsPerPageOptions={ADMIN_TABLE_PAGE_SIZE_OPTIONS}
                onPageChange={(_, nextPage) => setPage(nextPage)}
                onRowsPerPageChange={(event) => {
                  setRowsPerPage(Number(event.target.value));
                  setPage(0);
                }}
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </>
  );
};

const ModelsTab: React.FC<{ data: TokenUsageOverview }> = ({ data }) => {
  const theme = useTheme();
  const totalCost = sumBy(data.byModel, 'estimatedCostUsd');
  return (
    <>
      <MetricCardRow
        metrics={[
          { label: 'Total Cost (USD)', value: `$${totalCost.toFixed(2)}` },
          { label: 'Model Calls', value: sumBy(data.byModel, 'callCount') },
          { label: 'Input Tokens', value: sumBy(data.byModel, 'totalInputTokens') },
          { label: 'Output Tokens', value: sumBy(data.byModel, 'totalOutputTokens') },
          { label: 'Cache Read Tokens', value: sumBy(data.byModel, 'totalCacheReadTokens') },
          { label: 'Cache Write Tokens', value: sumBy(data.byModel, 'totalCacheWriteTokens') },
          { label: 'Sessions', value: sumBy(data.byModel, 'sessionCount') },
        ]}
      />
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <AdminBarChart
            labels={map(data.byModel, (m) => `${m.model} (${m.provider})`)}
            values={map(data.byModel, 'totalTokens')}
            title="Total Tokens by Model"
            horizontal
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <AdminBarChart
            labels={map(data.byModel, (m) => `${m.model} (${m.provider})`)}
            values={map(data.byModel, 'estimatedCostUsd')}
            title="Estimated Cost (USD) by Model"
            color={theme.palette.error.main}
            horizontal
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <AdminBarChart
            labels={map(data.byModel, (m) => `${m.model} (${m.provider})`)}
            values={map(data.byModel, 'callCount')}
            title="Model Calls by Model"
            color={theme.palette.secondary.main}
            horizontal
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <AdminBarChart
            labels={map(data.byModel, (m) => `${m.model} (${m.provider})`)}
            values={map(data.byModel, 'totalCacheReadTokens')}
            title="Cache Read Tokens by Model"
            color={theme.palette.success.main}
            horizontal
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <AdminBarChart
            labels={map(data.byModel, (m) => `${m.model} (${m.provider})`)}
            values={map(data.byModel, 'totalInputTokens')}
            title="Input Tokens by Model"
            color={theme.palette.info.main}
            horizontal
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <AdminBarChart
            labels={map(data.byModel, (m) => `${m.model} (${m.provider})`)}
            values={map(data.byModel, 'totalOutputTokens')}
            title="Output Tokens by Model"
            color={theme.palette.warning.main}
            horizontal
          />
        </Grid>
      </Grid>
    </>
  );
};

const TAB_CONFIG: { value: TabValue; label: string; icon: React.ReactElement }[] = [
  { value: 'overview', label: 'Overview', icon: <InsightsRoundedIcon /> },
  { value: 'users', label: 'Users', icon: <GroupRoundedIcon /> },
  { value: 'tools', label: 'Tools', icon: <StorageRoundedIcon /> },
  { value: 'agents', label: 'Agents', icon: <SmartToyRoundedIcon /> },
  { value: 'models', label: 'Models', icon: <DeviceHubRoundedIcon /> },
  { value: 'studies', label: 'Studies', icon: <ScienceRoundedIcon /> },
  { value: 'sessions', label: 'Sessions', icon: <TimelineRoundedIcon /> },
];

const DAYS_OPTIONS = [7, 14, 30, 60, 90, 180, 365];

export const CopilotAdmin = wrapAdminPage(() => {
  const [activeTab, setActiveTab] = useState<TabValue>('overview');
  const [days, setDays] = useState(30);
  const navigateBackFromAdmin = useNavigateBackFromAdmin();

  const { data, isLoading, isError, error } = useTokenUsageOverview(days);
  const { getUserNameById } = useUsersById();

  const getUserLabel = (user: UserTokenRow) => getUserDisplayName(user, getUserNameById);

  return (
    <Box sx={{ py: 3 }}>
      <Grid container justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Grid item>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton onClick={navigateBackFromAdmin} size="small">
              <ArrowBackRoundedIcon />
            </IconButton>
            <Typography variant="h4" fontWeight={600}>
              Copilot Token Usage
            </Typography>
          </Box>
        </Grid>
        <Grid item>
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel>Time Range</InputLabel>
            <Select value={days} label="Time Range" onChange={(event) => setDays(Number(event.target.value))}>
              {map(DAYS_OPTIONS, (d) => (
                <MenuItem key={d} value={d}>
                  Last {d} days
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
          <CircularProgress />
        </Box>
      )}

      {isError && <Alert severity="error">Failed to load token usage data: {String(error)}</Alert>}

      {data && (
        <>
          <SummaryCards data={data} />

          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
            <Tabs value={activeTab} onChange={(_, value) => setActiveTab(value)} variant="scrollable">
              {map(TAB_CONFIG, (tab) => (
                <Tab key={tab.value} value={tab.value} label={tab.label} icon={tab.icon} iconPosition="start" />
              ))}
            </Tabs>
          </Box>

          {activeTab === 'overview' && <OverviewTab data={data} getUserLabel={getUserLabel} />}
          {activeTab === 'users' && <UsersTab data={data} getUserLabel={getUserLabel} />}
          {activeTab === 'tools' && <ToolsTab data={data} />}
          {activeTab === 'agents' && <AgentsTab data={data} />}
          {activeTab === 'models' && <ModelsTab data={data} />}
          {activeTab === 'studies' && <StudiesTab data={data} />}
          {activeTab === 'sessions' && <SessionsTab data={data} />}
        </>
      )}
    </Box>
  );
});

export default CopilotAdmin;
